import { FC } from 'react';
import { Prompt } from '@/components/MainContent';
import { useAuth } from '@/contexts/AuthContext';

interface PromptTabNavigationProps {
  isLoggedIn: boolean;
  activeTab: 'current' | 'playground' | 'versions';
  prompt: Prompt;
  setActiveTab: (tab: 'current' | 'playground' | 'versions') => void;
  setShowAddToCollectionModal: (show: boolean) => void;
  promptInCollections?: Set<number>;
  isCheckingCollections?: boolean;
}

const PromptTabNavigation: FC<PromptTabNavigationProps> = ({ 
  isLoggedIn, 
  activeTab, 
  prompt, 
  setActiveTab, 
  setShowAddToCollectionModal,
  promptInCollections = new Set(),
  isCheckingCollections = false
}) => {
  const { user } = useAuth();
  const isOwner = user?.id === prompt?.user_id;
  
  // Determine button text based on whether prompt is in collections
  const getButtonText = () => {
    if (isCheckingCollections) {
      return 'Checking...';
    }
    if (promptInCollections.size > 0) {
      return 'Modify Collection Assignments';
    }
    return 'Add to Collections';
  };
  
  const getButtonIcon = () => {
    if (promptInCollections.size > 0) {
      return (
        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      );
    }
    return (
      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
    );
  };
  return (
    <div className="border-b border-gray-200">
      <nav className="flex space-x-8 px-6 justify-between">
        <div className='flex space-x-8'>
          <button
            onClick={() => setActiveTab('current')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'current'
              ? 'border-brand-electric-blue text-brand-electric-blue'
              : 'border-transparent text-gray-500 hover:text-brand-dark-blue hover:border-gray-300'
              }`}
          >
            Overview
          </button>
          {isOwner && <button
            onClick={() => setActiveTab('playground')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'playground'
              ? 'border-brand-electric-blue text-brand-electric-blue'
              : 'border-transparent text-gray-500 hover:text-brand-dark-blue hover:border-gray-300'
              }`}
          >
            Playground
          </button>}
          <button
            onClick={() => setActiveTab('versions')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'versions'
              ? 'border-brand-electric-blue text-brand-electric-blue'
              : 'border-transparent text-gray-500 hover:text-brand-dark-blue hover:border-gray-300'
              }`}
          >
            History
          </button>
        </div>
        <div className='flex items-center md:w-1/3 lg:w-[30%] xl:w-1/4 my-2'>
          {isLoggedIn && (
            <button
              onClick={() => setShowAddToCollectionModal(true)}
              className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-brand-dark-blue border border-transparent rounded-md hover:bg-brand-electric-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue"
            >
              {getButtonIcon()}
              {getButtonText()}
            </button>
          )}
        </div>
      </nav>
    </div>
  );
};

export default PromptTabNavigation;
